// ─── useVisualizeData ─────────────────────────────────────────────────────────
// Fetches time-series for each selected parameter over the active date range,
// aligns them by timestamp, and builds the matrix consumed by react-spreadsheet.
//
// Layout (matches the XD design):
//   row 1 : ['',          Site,   Site,   …]
//   row 2 : ['',          Device, Device, …]
//   row 3 : ['Datetime',  Param (Unit), …]
//   row 4+: [ datetime,   value,  value,  …]   ← one value column per parameter,
//                                                aligned on the shared Datetime.
//
// Long ranges are aggregated server-side (see fetchGraphData interval/agg).
// ──────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react'
import { projectsApi } from '../../../lib/projectsApi'

// Map the D/W/M/Y duration to an aggregation interval. Month/Year use DAY
// buckets so every date that has data shows as its own row (a coarser 'week'
// would collapse sparse data into a single row). Day = full day; Week = hourly.
const PERIOD_INTERVAL = { D: 'raw', W: 'hour', M: 'day', Y: 'day' }

const pad = (n) => String(n).padStart(2, '0')

// Format a UTC-ms timestamp shifted by the tz offset the API reports, so the
// grid shows the user's local clock time. `precision` controls the granularity
// of the label (and therefore the row-merge key): aggregated day/week/month
// views show date-only, hourly shows the hour, raw shows the minute.
function formatTs(ms, offsetSeconds = 0, precision = 'minute') {
  const d = new Date(ms + offsetSeconds * 1000)
  const date = `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`
  if (precision === 'date') return date
  if (precision === 'hour') return `${date} ${pad(d.getUTCHours())}:00`
  return `${date} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`
}

// Label granularity for each aggregation interval.
const INTERVAL_PRECISION = { raw: 'minute', hour: 'hour', day: 'date', week: 'date', month: 'date' }

const ro = (value) => ({ value, readOnly: true })

export function useVisualizeData(selectedParameters = [], dateRangeGraph, activePeriod) {
  const [state, setState] = useState({ data: [], series: [], loading: false, error: null })

  // Stable dependency key — refetch when the selected ids, range, or period change.
  const idsKey = selectedParameters.map((p) => p.id).join(',')
  const from = dateRangeGraph?.from
  const to = dateRangeGraph?.to

  useEffect(() => {
    let cancelled = false

    if (!selectedParameters.length) {
      setState({ data: [], series: [], loading: false, error: null })
      return
    }

    const interval = PERIOD_INTERVAL[activePeriod] || 'raw'
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone

    setState((s) => ({ ...s, loading: true, error: null }))

    Promise.all(
      selectedParameters.map((p) =>
        projectsApi
          .fetchGraphData(p.id, { fromDate: from, toDate: to, timezone: tz, interval, agg: 'avg' })
          .then((res) => ({ param: p, res }))
          .catch((err) => ({ param: p, error: err }))
      )
    )
      .then((results) => {
        if (cancelled) return

        const offset = results.find((r) => r.res?.tzOffsetSeconds != null)?.res?.tzOffsetSeconds || 0

        // Merge points into ONE row per display timestamp. Parameters on the
        // same logger are often recorded a few seconds apart, so keying on the
        // raw ms would split a single timestamp into several rows (each holding
        // one parameter's value). Key on the minute-precision label instead.
        const labelPrecision = INTERVAL_PRECISION[interval] || 'minute'
        const rowMap = new Map()
        results.forEach(({ res }, i) => {
          const prec = res?.precision ?? 0
          ;(res?.data || []).forEach((pt) => {
            if (pt.y === null || pt.y === undefined) return
            const label = formatTs(pt.x, offset, labelPrecision)
            let row = rowMap.get(label)
            if (!row) {
              row = { sortX: pt.x, vals: new Array(results.length).fill('') }
              rowMap.set(label, row)
            }
            if (pt.x < row.sortX) row.sortX = pt.x
            row.vals[i] = Number(pt.y).toFixed(prec)
          })
        })
        const sortedRows = [...rowMap.entries()].sort((a, b) => a[1].sortX - b[1].sortX)

        // Header rows
        const siteRow = ['', ...results.map(({ param }) => param.siteName || '')]
        const deviceRow = ['', ...results.map(({ param }) => param.deviceName || '')]
        const paramRow = [
          'Datetime',
          ...results.map(({ param, res }) => {
            const unit = res?.unit || param.unit || ''
            const name = res?.parameterName || param.name || ''
            return unit ? `${name} (${unit})` : name
          }),
        ]

        const dataRows = sortedRows.map(([label, row]) => [label, ...row.vals])

        const matrix = [siteRow, deviceRow, paramRow, ...dataRows].map((row) => row.map(ro))

        // Series for the chart popup (reuses the same fetched data).
        const series = results.map(({ param, res }) => ({
          id: param.id,
          name: res?.parameterName || param.name,
          unit: res?.unit || param.unit || '',
          siteName: param.siteName || '',
          deviceName: param.deviceName || '',
          precision: res?.precision ?? 0,
          points: (res?.data || []).filter((pt) => pt.y !== null && pt.y !== undefined),
        }))

        const firstError = results.find((r) => r.error)?.error
        setState({ data: matrix, series, loading: false, error: firstError ? 'Some parameters failed to load.' : null })
      })
      .catch((err) => {
        if (!cancelled) setState({ data: [], series: [], loading: false, error: err.message || 'Failed to load data' })
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, from, to, activePeriod])

  return state
}
